import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, sendPhoneCode, verifyPhoneCode, type AuthMe } from './api';

type AuthModalProps = {
  onClose: () => void;
  onAuthenticated: (account: Extract<AuthMe, { authenticated: true }>) => void;
};

function messageFromError(cause: unknown) {
  if (cause instanceof ApiError) return cause.message;
  return cause instanceof Error ? cause.message : '登录失败，请稍后重试。';
}

export function AuthModal({ onClose, onAuthenticated }: AuthModalProps) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const busy = sending || verifying;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [busy, onClose]);

  async function handleSendCode() {
    if (sending || cooldown > 0) return;
    setError('');
    setSending(true);
    try {
      const result = await sendPhoneCode(phone);
      setChallengeId(result.challengeId);
      setCooldown(60);
    } catch (cause) {
      setError(messageFromError(cause));
    } finally {
      setSending(false);
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    if (verifying || !challengeId) return;
    setError('');
    setVerifying(true);
    try {
      const account = await verifyPhoneCode(challengeId, code);
      onAuthenticated(account);
    } catch (cause) {
      setError(messageFromError(cause));
    } finally {
      setVerifying(false);
    }
  }

  return createPortal(
    <div className="modal-backdrop auth-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <section className="continuation-modal auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <div className="modal-heading">
          <div><span className="section-kicker">账户登录</span><h2 id="auth-modal-title">登录你的故事<br /><em>保存你的故事。</em></h2></div>
          <button className="modal-close" disabled={busy} type="button" onClick={onClose} aria-label="关闭登录窗口">×</button>
        </div>
        <p className="modal-description">登录后，你的小说、阅读进度和配图会跟随账号保存。手机号只用于验证码登录。</p>
        <form className="auth-form" onSubmit={handleVerify}>
          <label className="modal-input-label">手机号<input autoComplete="tel" inputMode="tel" maxLength={30} placeholder="请输入手机号" required value={phone} onChange={(event) => { setPhone(event.target.value); setChallengeId(''); }} /></label>
          <div className="auth-code-row">
            <label className="modal-input-label">验证码<input autoComplete="one-time-code" inputMode="numeric" maxLength={6} pattern="[0-9]{4,6}" placeholder="短信验证码" required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
            <button className="auth-send-button" disabled={busy || cooldown > 0 || !phone.trim()} type="button" onClick={() => void handleSendCode()}>{sending ? '发送中…' : cooldown > 0 ? `${cooldown}s 后重发` : challengeId ? '重新发送' : '获取验证码'}</button>
          </div>
          {challengeId && <p className="auth-hint" role="status">验证码已发送，请在 5 分钟内完成登录。</p>}
          {error && <p className="auth-error" role="alert">{error}</p>}
          <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>稍后登录</button><button className="modal-submit" disabled={busy || !challengeId || code.length < 4} type="submit">{verifying ? '正在登录…' : '登录并保存 →'}</button></div>
        </form>
      </section>
    </div>,
    document.body,
  );
}
